/**
 * Permission catalogue — the SINGLE source of truth for RBAC (ADR-0008, AUTH_RBAC_ARCHITECTURE §3).
 *
 * Seeded into iam.permissions by supabase/seed/reference (generated), documented in
 * docs/J-security/PERMISSION_CATALOG.md (generated), and consumed by the web app's useCan().
 * A parity test fails if the database seed, this file and the generated document disagree.
 *
 * Code format: <module>.<resource>[.<sub>].<action>
 * Sensitivity:
 *   NORMAL    — ordinary capability
 *   SENSITIVE — touches personal or financial data; reads are logged
 *   CRITICAL  — moves money, changes access, overrides a control; MFA + reason + audit
 */

export type Sensitivity = 'NORMAL' | 'SENSITIVE' | 'CRITICAL';

export interface PermissionDef {
  readonly code: string;
  readonly module: string;
  readonly description: string;
  readonly sensitivity: Sensitivity;
}

const p = (code: string, description: string, sensitivity: Sensitivity = 'NORMAL'): PermissionDef => ({
  code,
  module: code.split('.')[0] as string,
  description,
  sensitivity,
});

export const PERMISSIONS = [
  // ── Overview, attention, notifications ───────────────────────────────────────────────
  p('overview.view', 'View the Management Control Center'),
  p('attention.view', 'View attention items in scope'),
  p('attention.manage', 'Acknowledge, assign and resolve attention items'),
  p('notifications.view', 'View own notifications'),
  p('quality.milk_tests.view', 'See every milk test of the plant and the farms on one board, with the card photographs, the pads and where each was taken (the owner, 2026-10-01)', 'SENSITIVE'),
  p('notifications.field_notice.send', 'Send and cancel notices (a notification and a moving strip) to the Rider, Plant and Farm apps from Supply › Notices'),
  p('notifications.manage', 'Switch a kind of notification on or off, for the plant’s phones, the delivery app and the farm (the owner, 2026-10-01: every notification in one place)', 'SENSITIVE'),

  // ── Identity and access (Administration) ────────────────────────────────────────────
  p('iam.users.view', 'View user accounts', 'SENSITIVE'),
  p('iam.users.manage', 'Invite, activate, suspend and deactivate user accounts', 'CRITICAL'),
  p('iam.roles.view', 'View roles and their permissions'),
  p('iam.roles.manage', 'Create roles and change their permissions', 'CRITICAL'),
  p('iam.grants.manage', 'Grant and revoke roles and scopes', 'CRITICAL'),
  p('iam.sessions.view', 'View sessions and login activity', 'SENSITIVE'),
  p('iam.applications.view', 'View application access, including field-app mirrors'),
  p('iam.applications.manage', 'Change application access', 'CRITICAL'),
  p('iam.api_clients.view', 'View API clients and key metadata'),
  p('iam.api_keys.manage', 'Issue and revoke API keys', 'CRITICAL'),

  // ── Audit, approvals, configuration, system ─────────────────────────────────────────
  p('audit.view', 'View audit trail and business events', 'SENSITIVE'),
  p('audit.sensitive_access.view', 'View the sensitive-data access log', 'SENSITIVE'),
  p('approvals.view', 'View the approvals inbox and history'),
  p('approvals.policies.manage', 'Configure approval policies', 'CRITICAL'),
  p('config.view', 'View configuration values and versions'),
  p('config.manage', 'Schedule and activate configuration versions', 'CRITICAL'),
  p('system.logs.view', 'View system, job and API logs'),
  p('jobs.alerts.view', 'Be told in the inbox when the day’s automatic work fails (preparing tomorrow, the cutoff), with a Run now (owner, 2026-10-02, Q0.6)'),

  // ── Master data ─────────────────────────────────────────────────────────────────────
  p('masterdata.view', 'View master data'),
  p('masterdata.org.manage', 'Manage organisation, legal entities, business units, departments and cities', 'CRITICAL'),
  p('masterdata.locations.manage', 'Manage locations and the site hierarchy'),
  p('masterdata.products.manage', 'Manage products, variants and SKUs'),
  p('masterdata.uom.manage', 'Manage units of measure and conversion factors'),
  p('masterdata.reason_codes.manage', 'Manage reason-code vocabularies'),
  p('masterdata.qr.manage', 'Allot, assign, retire QR identities and print labels'),
  p('qr.labels.doors', 'Print and reprint door labels and gate stickers (JDH, CUS), a reprint with its reason (v7 Q5.3)'),
  p('qr.labels.containers', 'Print and reprint container labels: bottles, jars, barrels, crates, cans (BTL, JAR, BAR, CRT, CAN), a reprint with its reason (v7 Q5.3)'),
  p('masterdata.finance.manage', 'Manage payment methods and expense categories', 'SENSITIVE'),
  p('masterdata.import', 'Run master-data imports (dry-run and apply)', 'SENSITIVE'),
  p('pricing.view', 'View price lists and charge policies'),
  p('pricing.manage', 'Change prices and charge policies (effective-dated, approval)', 'SENSITIVE'),

  // ── Integration ─────────────────────────────────────────────────────────────────────
  p('integration.view', 'View sync health, inbound records, entity links and conflicts'),
  p('integration.manage', 'Configure connectors, sources and authority rules', 'CRITICAL'),
  p('integration.ingest', 'Submit source envelopes to the ingest API (service accounts only)'),
  p('integration.reconcile', 'Run reconciliation rules'),
  p('integration.resolve', 'Resolve sync conflicts and reconciliation items', 'SENSITIVE'),

  // ── Farm ────────────────────────────────────────────────────────────────────────────
  p('farm.locus.view', 'View Major and Minor Loci'),
  p('farm.locus.manage', 'Edit ERP-owned locus attributes'),
  p('farm.locus.approve', 'Approve or reject Minor Locus onboarding'),
  p('farm.locus.onboard', 'Onboard a new Minor Locus under a Major Locus the person manages, from the Farm app (the owner, 2026-10-05)'),
  p('farm.locus.kyc.view', 'View Minor Locus KYC documents and identifiers', 'SENSITIVE'),
  p('farm.collection.view', 'View collections, corrections and rounds'),
  p('farm.collection.record', 'Open collection sessions and record or correct collections — only at sites where the ERP captures (CD-63)'),
  p('farm.sop.view', 'View SOP templates and compliance'),
  p('farm.sop.manage', 'Author and publish SOP template versions'),
  p('farm.rate.view', 'View supplier rates'),
  p('farm.rate.manage', 'Change supplier rates (effective-dated, approval)', 'SENSITIVE'),
  p('farm.bill.view', "See a farm's bills, payments and what each farmer is owed", 'SENSITIVE'),
  p('farm.bill.make', "Make a farmer's bill for a day, a month or any dates", 'SENSITIVE'),
  p('farm.payment.record', 'Record a payment to a farmer from the Farm app, and reverse one not yet verified', 'SENSITIVE'),
  p('farm.batch.view', 'View batches and their sources'),
  p('farm.batch.manage', 'Create batches and put them into a chiller — only at sites where the ERP captures (CD-63)'),
  p('farm.dispatch.view', 'View allocations, fills, shipments and transit'),
  p('farm.dispatch.manage', 'Fill dispatch barrels, dispatch shipments and mark arrival — only at sites where the ERP captures (CD-63)'),

  // ── Quality ─────────────────────────────────────────────────────────────────────────
  p('quality.view', 'View quality tests, observations and evidence'),
  p('quality.test', 'Record ERP-side quality tests (plant, cases)'),
  p('quality.approve', 'Approve quality outcomes and incident closure'),
  p('quality.override', 'Override a quality result (approval, reason)', 'CRITICAL'),
  p('quality.config.manage', 'Manage quality specifications and thresholds', 'SENSITIVE'),
  p('quality.incident.manage', 'Open, investigate and close quality incidents'),
  p('quality.hold.manage', 'Place and release a quality hold on a batch, barrel, chiller or lot', 'SENSITIVE'),
  p('quality.result.propose', 'Propose a quality result from a device, an image or a model — it changes nothing until a person accepts it'),
  p('quality.exception.forward', 'Send milk that failed a quality check forward as an explicit exception, with a reason that travels with it (never for an adulterant)', 'SENSITIVE'),
  p('quality.exception.approve', 'Approve a quality exception raised at a plant', 'CRITICAL'),
  p('quality.adulteration.override', 'Ask for, and approve, the override of an adulterant positive after a re-test. For ERP administrators only; never a field role', 'CRITICAL'),
  p('quality.disposition.manage', 'Decide in the ERP what happens to failed or rejected milk: another use, disposal, waste or cattle feed', 'SENSITIVE'),

  // ── Plant ───────────────────────────────────────────────────────────────────────────
  p('plant.view', 'View plant operations, stock and lots'),
  p('plant.inward.view', 'View plant inward: receipts, pours, transit loss'),
  p('plant.inward.reconcile', 'Reconcile plant inward against dispatches'),
  p('plant.inward.receive', 'Weigh in received barrels and pour them — only at plants where the ERP captures (CD-63)'),
  p('plant.filling.manage', 'Open, record and close filling lots'),
  p('plant.production.manage', 'Manage processing jobs'),
  p('plant.coldroom.manage', 'Transfer finished goods to cold room and hub handovers'),
  p('plant.bottle.mark_ready', 'Mark a bottle ready to fill at the desk, without the wash scanner, with a reason'),
  p('plant.wastage.record', 'Record wastage and loss (approval)', 'SENSITIVE'),
  p('plant.records.view', 'View the plant’s own books: what a plant wrote down about its own day'),
  p('plant.records.declare', 'Declare a record in the plant’s own books — held by the plant application, not by an ERP desk, and only where the field application captures (CD-63)'),
  p('farm.barrel.tare', 'Re-weigh a barrel’s tare at the farm, and register a barrel that has no label, as ERP records (the owner, 2026-09-29: the tare is edited at both ends, farm and ERP)'),
  p('farm.kyc.reveal', 'See and type a farmer’s full Aadhaar, PAN, bank account and UPI id; every reveal is logged (the owner, 2026-09-29: the ERP admin only)', 'CRITICAL'),
  p('farm.collection.correct', "Correct a farmer's collection from the ERP before it is batched, or void a duplicate (with a reason and the second factor); the phone is told", 'SENSITIVE'),
  p('farm.batch.adjust', 'Record a reasoned adjustment on a consolidated production once its milk can no longer be corrected at the can (second factor)', 'SENSITIVE'),
  p('plant.dry_count.approve', 'Approve or reject a count of the dry room before its difference adjusts stock (Plant v3; the owner, 2026-09-29: at the desk, and on the phone by the plant manager)', 'SENSITIVE'),
  p('plant.records.void', 'Void a record of the plant’s books — never delete: a void is a new record naming the one it withdraws, with a reason (Plant v3 slice 7; the owner: at the desk, and on the phone by the manager)', 'SENSITIVE'),
  p('plant.requests.approve', 'Approve, reject or mark issued a stock request from a plant (the owner, 2026-10-04: at the desk, and on the phone by the plant manager)'),
  p('plant.message.send', 'Send an instruction or a broadcast to the plant’s phones (the owner, 2026-09-30: push slice N2)'),
  p('plant.custody.declare', 'Declare custody of barrels in transit — picked up at the bus stand, handed over at the plant. The transit agent’s only permission; it opens no other book and reads nothing'),

  // ── Inventory ───────────────────────────────────────────────────────────────────────
  p('inventory.view', 'View stock balances, ledger and traces'),
  p('inventory.adjust', 'Request stock adjustments (approval)', 'SENSITIVE'),
  p('inventory.transfer', 'Record ERP-originated stock transfers'),
  p('inventory.verify', 'Record physical stock verifications'),
  p('inventory.reserve', 'Hold and release reservations'),
  p('inventory.movement.reverse', 'Reverse a stock movement that was posted in error (reason; approval where a policy exists)', 'CRITICAL'),
  p('inventory.loss.manage', 'Review a loss of stock in the ERP: acknowledge it, ask the site to report, clear it or write it off, with a reason', 'SENSITIVE'),

  // ── Assets and devices ──────────────────────────────────────────────────────────────
  p('assets.view', 'View the asset register'),
  p('assets.manage', 'Manage assets'),
  p('assets.barrel.manage', 'Manage barrel master, tare and lifecycle (ERP-owned fields)'),
  p('assets.chiller.manage', 'Manage chiller units and compartments (ERP-owned fields)'),
  p('assets.bottle.manage', 'Manage the bottle register: sizes, registration in runs and retirement. Custody stays with the delivery application'),
  p('assets.assign', 'Assign assets to people or locations'),
  p('assets.maintenance.manage', 'Plan and record maintenance and inspections'),
  p('devices.view', 'View devices, telemetry and alarms'),
  p('devices.manage', 'Register, assign and retire devices', 'SENSITIVE'),

  // ── Customers, subscriptions, orders ────────────────────────────────────────────────
  p('customer.view', 'View customers (without restricted personal data)'),
  p('customer.create', 'Create customers'),
  p('customer.edit', 'Edit ERP-owned customer attributes'),
  p('customer.day.manage', "Change one customer's price for a day, and record one customer's day from the office (every CRM user; the owner, 2026-10-08; migration 128600)", 'SENSITIVE'),
  p('customer.pii.view', 'View customer phone numbers, addresses and geo pins', 'SENSITIVE'),
  p('customer.import', 'Import customers and subscriptions', 'SENSITIVE'),
  p('bulk.view', 'See the B2B customers, their challans and bills (the owner, 2026-10-13; migration 221)'),
  p('bulk.manage', 'Add and change B2B customers: name, phone, GSTIN, rate per litre (the owner, 2026-10-13; migration 221)', 'SENSITIVE'),
  p('subscription.view', 'View subscriptions, lines and dated changes'),
  p('subscription.manage', 'Draft, activate, amend, pause and resume a standing agreement'),
  p('subscription.cancel', 'Cancel a standing agreement — it is final, and it ends a customer commitment', 'SENSITIVE'),
  p('order.view', 'View orders'),
  p('order.create', 'Create extra and B2B orders'),
  p('order.edit', 'Amend the lines of a planned order'),
  p('order.cancel', 'Cancel a planned or confirmed order', 'SENSITIVE'),
  p('order.late_change', "Change a customer's day after the cut-off (the CRM manager, owner 28 Sep); others submit it for approval"),
  // The owner, 2026-10-06: the CRM manager (not customer service) changes status, items, pattern, discounts and paid marks.
  p('customer.status.manage', 'Pause, resume or end a customer from a date (the CRM manager, the owner 2026-10-06)', 'SENSITIVE'),
  p('customer.subscription.manage', "Change a customer's variant, pattern or quantity from a date (the CRM manager)", 'SENSITIVE'),
  p('customer.discount.manage', "Give or end a customer's discount (the CRM manager)", 'SENSITIVE'),
  p('customer.payment.mark', "Mark a customer's month paid, with a reason (the CRM manager)", 'SENSITIVE'),
  p('lead.view', 'View leads'),
  p('lead.manage', 'Create, qualify and convert leads'),
  p('customer.payment.view', 'View customer payments'),
  p('customer.payment.create', 'Record customer payments', 'SENSITIVE'),
  p('bottles.view', 'View bottle custody, balances and trails'),
  p('bottles.manage', 'Record ERP-side bottle outcomes (lost, waived)', 'SENSITIVE'),

  // ── Delivery and logistics ──────────────────────────────────────────────────────────
  p('delivery.view', 'View delivery days, deliveries and proof of delivery'),
  p('delivery.plan.manage', 'Build, amend and confirm the delivery plan for a route and a date'),
  p('delivery.record', 'Record what was handed over at a door'),
  // The evidence is a photograph of somebody's doorway, or their signature. It is not ordinary delivery data.
  p('delivery.pod.view', 'View proof-of-delivery evidence', 'SENSITIVE'),
  // Skipping the door label is allowed and audited, never silent — and it is not a thing every rider may do.
  p('delivery.qr.bypass', 'Complete a stop without scanning the door label, with a reason', 'SENSITIVE'),
  p('delivery.exception.manage', 'Record and resolve delivery exceptions'),
  // Correcting what somebody else recorded is not recording. It overrules a first-hand account, so it has
  // its own permission and its own reason taxonomy.
  p('delivery.correct', 'Correct a settled delivery outcome, with a reason', 'SENSITIVE'),
  p('delivery.day_close.sign', 'Sign off a delivery day as closed (the owner, 2026-10-03: the owner signs)', 'SENSITIVE'),
  // The owner, 2026-10-06: 1–5 Oct came into the ERP from the team's sheet — an office round, no stock, no rider pay.
  p('delivery.office_record', "Record a day gone by from the office's delivery sheet (no stock, no rider pay; the owner, 2026-10-06)", 'SENSITIVE'),
  p('bottles.custody.correct', 'Correct a container’s recorded custody, with a reason', 'SENSITIVE'),
  p('delivery.reassign', 'Move a delivery from one round to another'),
  p('delivery.bulk', 'Apply a move or a status to several stops at once'),
  p('delivery.claims.decide', 'Approve or refuse a rider’s claim on a round'),
  p('customer.status.publish', 'Publish approved customer status changes together', 'SENSITIVE'),
  // Reads across every master, so it is implied by none of the individual view permissions.
  p('datahealth.view', 'See what is wrong with the data before the round is built'),
  // Allocating a customer code is not creating a customer: it is taking the next number in a sequence
  // that is printed on stickers and is the join key for deliveries, reports and bottle custody.
  p('customer.code.allocate', 'Take the next customer code from the ERP’s own sequence', 'SENSITIVE'),
  p('delivery.reconcile', 'Reconcile route returns and delivery exceptions'),
  p('delivery.reports.manage', 'Triage rider field reports'),
  // A rider's login is a credential: making one, resetting or locking its PIN and approving its phone decide who may
  // record deliveries (Delivery D2, CD-115, CD-116).
  p('delivery.riders.manage', 'Create rider logins, reset or lock a PIN, approve a rider’s phone', 'SENSITIVE'),
  p('delivery.demo.manage', 'Turn a rider’s read-only demo login on or off and set the demo PIN (real customer data becomes visible)', 'SENSITIVE'),
  p('logistics.routes.view', 'View routes and stop plans'),
  p('logistics.routes.manage', 'Manage routes and stop plans'),
  p('logistics.trips.view', 'View trips and shifts'),
  p('logistics.trips.manage', 'Create trips, assign riders and vehicles, and close a round'),
  p('logistics.gps.view', 'View location history of people', 'SENSITIVE'),
  p('logistics.vehicles.view', 'View vehicles'),
  p('logistics.vehicles.manage', 'Manage vehicles and assignments'),
  p('logistics.fuel.manage', 'Record fuel and vehicle costs'),

  // ── Customer service and communication ──────────────────────────────────────────────
  p('service.case.view', 'View cases and complaints'),
  p('service.case.manage', 'Create, assign and resolve cases'),
  p('service.conversation.view', 'View WhatsApp and voice conversations', 'SENSITIVE'),
  p('service.whatsapp.api', 'Call the WhatsApp integration APIs (service accounts only)'),

  // ── Finance ─────────────────────────────────────────────────────────────────────────
  p('finance.view', 'View finance dashboards and registers'),
  p('finance.ledger.view', 'View ledgers and journal detail', 'SENSITIVE'),
  p('finance.journal.post', 'Post manual journal entries', 'CRITICAL'),
  p('finance.invoice.issue', 'Generate and issue invoices', 'SENSITIVE'),
  p('finance.payment.reverse', 'Reverse payments (approval)', 'CRITICAL'),
  p('finance.charge.waive', 'Waive charges (approval)', 'SENSITIVE'),
  // The owner, 2026-10-06: opening balances (owed on 30 Sep), curdled-milk credits and one-off charges (the CRM manager).
  p('finance.customer.adjust', "Enter or void a customer's opening balance, credit or one-off charge (the CRM manager, the owner 2026-10-06)", 'SENSITIVE'),
  // Finance Phase 2 (146400): finalise and void milk bills, credit and debit notes, the late charge and dated GST rates.
  p('finance.bill.finalise', "Finalise the month's milk bills (numbers, freeze, ledger), and issue final bills", 'SENSITIVE'),
  p('finance.bill.void', 'Void a finalised milk bill (its number stays in the sequence, marked VOID) and reissue it', 'SENSITIVE'),
  p('finance.note.issue', 'Issue or void a credit or debit note against a bill', 'SENSITIVE'),
  p('finance.note.approve', 'Approve or reject a credit or debit note above the approval threshold', 'SENSITIVE'),
  p('finance.late_charge.manage', "Manage the late charge (waivers; the switch stays the owner's)", 'SENSITIVE'),
  p('finance.tax.manage', "Set GST rates by date and record the CA's confirmation", 'SENSITIVE'),
  p('finance.bill.send', 'Send finalised milk bills on WhatsApp and send one again with a reason', 'SENSITIVE'),
  p('finance.template.manage', 'Save a new version of a bill design from a date, or retire one not yet in force', 'SENSITIVE'),
  p('finance.expense.create', 'Submit expenses'),
  p('finance.expense.approve', 'Approve expenses', 'SENSITIVE'),
  p('finance.vendor.manage', 'Manage vendors and bills', 'SENSITIVE'),
  p('finance.costcenter.manage', 'Manage cost centres and allocation rules'),
  p('finance.period.close', 'Close and reopen fiscal periods', 'CRITICAL'),
  p('farmer.settlement.view', 'View farmer settlements and farmer ledger', 'SENSITIVE'),
  p('farmer.settlement.prepare', 'Prepare farmer settlements', 'SENSITIVE'),
  p('farmer.settlement.approve', 'Approve farmer settlements', 'CRITICAL'),
  p('farmer.payment.create', 'Record payments to farmers', 'CRITICAL'),

  // ── People, HR, workforce ───────────────────────────────────────────────────────────
  p('people.view', 'View people and employment (without restricted fields)'),
  p('people.create', 'Create people and candidates'),
  p('people.edit', 'Edit people and employment records'),
  p('people.salary.view', 'View salary structures and history', 'SENSITIVE'),
  p('people.salary.edit', 'Change salary structures (approval)', 'CRITICAL'),
  p('people.bank.view', 'View bank account details', 'SENSITIVE'),
  p('people.documents.view', 'View identity and employment documents', 'SENSITIVE'),
  p('people.documents.manage', 'Upload and verify documents', 'SENSITIVE'),
  p('people.offer.issue', 'Generate and issue offer letters', 'SENSITIVE'),
  p('people.exit.manage', 'Manage exits and clearance'),
  p('attendance.view', 'View attendance and calendars'),
  p('attendance.manage', 'Record and correct attendance'),
  p('shifts.manage', 'Manage work shifts and rosters'),
  p('leave.view', 'View leave requests and balances'),
  p('leave.request', 'Request leave for self'),
  p('leave.approve', 'Approve leave'),
  p('assignment.view', 'View operational assignments'),
  p('assignment.manage', 'Assign people to chillers, vehicles, routes and loci'),
  p('tasks.view', 'View tasks'),
  p('tasks.manage', 'Create and assign tasks'),
  p('training.view', 'View trainings and SOP acknowledgements'),
  p('training.manage', 'Manage trainings, sessions and SOP documents'),
  p('performance.view', 'View performance reviews, KPIs and targets'),
  p('performance.manage', 'Manage reviews, KPIs, targets and turnout audits'),
  p('incentive.rules.manage', 'Manage incentive schemes and rules (approval)', 'SENSITIVE'),
  p('earnings.view', 'View earning events and rider earnings', 'SENSITIVE'),
  p('earnings.adjust', 'Record manual bonuses and deductions (reason, audit)', 'SENSITIVE'),
  p('payroll.view', 'View payroll runs, lines and payslips', 'SENSITIVE'),
  p('payroll.calculate', 'Calculate payroll runs', 'SENSITIVE'),
  p('payroll.approve', 'Approve payroll runs', 'CRITICAL'),
  p('payroll.pay', 'Record payroll payments', 'CRITICAL'),

  // ── Reports and analytics ───────────────────────────────────────────────────────────
  p('reports.view', 'View reports in scope'),
  p('reports.export', 'Export reports (masked unless unmask permission held)', 'SENSITIVE'),
  p('analytics.view', 'View analytics dashboards'),
] as const satisfies readonly PermissionDef[];

export type PermissionCode = (typeof PERMISSIONS)[number]['code'];

export const PERMISSION_CODES: ReadonlySet<string> = new Set(PERMISSIONS.map((perm) => perm.code));

export function isPermissionCode(value: string): value is PermissionCode {
  return PERMISSION_CODES.has(value);
}

/** The brief's example permissions (BRIEF §14) — asserted present by test. */
export const BRIEF_EXAMPLE_PERMISSIONS = [
  'people.view',
  'people.create',
  'people.edit',
  'people.salary.view',
  'people.salary.edit',
  'customer.view',
  'customer.create',
  'customer.payment.view',
  'customer.payment.create',
  'payroll.view',
  'payroll.calculate',
  'payroll.approve',
  'payroll.pay',
  'inventory.view',
  'inventory.adjust',
  'inventory.transfer',
  'quality.view',
  'quality.test',
  'quality.approve',
  'quality.override',
  'audit.view',
] as const;
